<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-0">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" hide-details option="Last 365 days"/>
        </v-col>
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
        <v-col cols="3">
          <v-autocomplete label="Spirit" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...spirits]" v-model="spiritId" item-text="name" item-value="id" @change="totals()"/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Cask numbers" v-model="caskNumbers" dense outlined hide-details :items="['Current','Original']"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml(`Angel's share`, report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsAngelsShare',
  mixins: [printHtml],
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      siteId: null,
      spirits: [],
      spiritId: null,
      caskNumbers: 'Original',
      avgTotal: 0,
      avgAnnual: 0,
      casks: []
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    list() {
      return this.spiritId ? this.casks.filter(cask => cask.spiritId === this.spiritId) : this.casks;
    },
    sites() {
      return this.$store.getters.sites(true, {B266:true});
    }
  },
  async created() {
    this.$emit('name', 'Angel\'s share')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
    this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}])
    this.totals();
  },
  methods: {
    totals() {
      this.avgTotal = 0;
      this.avgAnnual = 0;
      if (!this.list.length)
        return;
      this.list.forEach(c => {this.avgTotal+=c.totalPercentLoss;this.avgAnnual+=c.annualPercentLoss});
      this.avgTotal /= this.list.length;
      this.avgAnnual /= this.list.length;
    },
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsAngelsShare($from:Date!,$to:Date!,$siteId:ID) {
                                             angelsShare(from:$from,to:$to,siteId:$siteId) {
                                               number,originalNumber,fillDate,emptyDate,fillLAA,lossLAA,spiritName,spiritId,totalPercentLoss,annualPercentLoss
                                             }
                                           }`,
                                          {from:this.from,to:this.to,siteId:this.siteId});
      if (!results.error) {
        this.casks = results.data.angelsShare;
        this.totals();
      }
      this.loading = false;
    },

    report() {
    const lines = [
                  `<style>
                    @page {size:landscape}
                    .reports-angles-share {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .reports-angles-share th {height:36px;padding:0 16px;vertical-align:top}
                    .reports-angles-share .spacer {height:18px}
                    .reports-angles-share td {height:36px;padding:0 16px;vertical-align:top}
                    .reports-angles-share .title {font-size:18px}
                    .reports-angles-share .sup-title {font-weight:normal;font-size:22px}
                    .reports-angles-share .sub-title {font-weight:normal;height:inherit;padding-bottom:16px;font-size:14px}
                    .reports-angles-share .text-left {text-align:left}
                    .reports-angles-share .text-right {text-align:right}
                    .reports-angles-share .font-weight-bold {font-weight:bold}
                    .reports-angles-share .total-row {font-weight:bold}
                  </style>
                   <table class="reports-angles-share">
                     <thead>
                        <tr>
                          <th colspan="8" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="8" class="title">Angel's share</th>
                        </tr>
                        <tr>
                           <th colspan="8" class="sub-title">${this.dateDisplay}</th>
                        </tr>
                        <tr>
                            <th width="5%" class="text-left">Cask</th>
                            <th width="25%" class="text-left">Spirit</th>
                            <th width="15%" class="text-left">Filled</th>
                            <th width="15%" class="text-left">Emptied</th>
                            <th width="10%" class="text-right">Fill LAA</th>
                            <th width="10%" class="text-right">LAA lost</th>
                            <th width="10%" class="text-right">Total</th>
                            <th width="10%" class="text-right">Annual</th>
                          </tr>
                      </thead>
                      <tbody>`
    ];
    for (const cask of this.list) {
      lines.push(`<tr>`);
      lines.push(`<td>${this.caskNumbers === 'Original' ? cask.originalNumber : cask.number}</td>`);
      lines.push(`<td>${cask.spiritName}</td>`);
      lines.push(`<td>${this.$formatDate(cask.fillDate)}</td>`);
      lines.push(`<td>${this.$formatDate(cask.emptyDate)}</td>`);
      lines.push(`<td class="text-right">${this.$number(cask.fillLAA,3)}</td>`);
      lines.push(`<td class="text-right">${this.$number(cask.lossLAA,3)}</td>`);
      lines.push(`<td class="text-right">${this.$number(cask.totalPercentLoss,1)}%</td>`);
      lines.push(`<td class="text-right">${this.$number(cask.annualPercentLoss,1)}%</td>`);
      lines.push(`</tr>`);
    }
    lines.push(`<tr :class="dark ? 'total-row-dark' : 'total-row'"><td colspan="6" class="text-right">Average</td><td class="text-right">${this.$number(this.avgTotal,1)}%</td><td class="text-right">${this.$number(this.avgAnnual,1)}%</td>`);
    lines.push(`</tbody>`);
    lines.push(`</table>`);
    return lines.join('');
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,,,,`,
                      `"Angel's share",,,,,,,`,
                      `"${this.dateDisplay}",,,,,,,`,
                      `"Cask","Spirit","Filled","Emptied","Fill LAA","LAA lost","Total","Annual"`
                    ];

      for (const cask of this.list)
        lines.push(`"${this.caskNumbers === 'Original' ? cask.originalNumber : cask.number}","${cask.spiritName}","${cask.fillDate}","${cask.emptyDate}","${this.$number(cask.fillLAA,3)}","${this.$number(cask.lossLAA,3)}","${this.$number(cask.totalPercentLoss,1)}","${this.$number(cask.annualPercentLoss,1)}"`);
      lines.push(`"Average",,,,,,"${this.$number(this.avgTotal,1)}","${this.$number(this.avgAnnual,1)}"`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Angels share.csv');
    }
  }
}
</script>